/**
 * Formatting utilities for displaying blockchain data
 */

import { formatUnits, parseUnits } from 'viem'

/**
 * Format token amount with proper decimals
 */
export function formatTokenAmount(
  amount: bigint,
  decimals: number = 18,
  maxDecimals: number = 4
): string {
  const formatted = formatUnits(amount, decimals)
  const num = parseFloat(formatted)
  
  if (num === 0) return '0'
  if (num < 0.0001) return '< 0.0001'
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(num)
}

/**
 * Format USD amount
 */
export function formatUSD(amount: number | bigint, decimals: number = 8): string {
  const num = typeof amount === 'bigint' 
    ? parseFloat(formatUnits(amount, decimals))
    : amount
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

/**
 * Format currency amount (EUR, GBP, etc.)
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EUR'
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format address (truncate middle)
 */
export function formatAddress(address: string, chars: number = 4): string {
  if (!address) return ''
  if (address.length <= chars * 2 + 2) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

/**
 * Format transaction hash (truncate middle)
 */
export function formatTxHash(hash: string): string {
  return formatAddress(hash, 6)
}

/**
 * Format date/timestamp
 */
export function formatDate(timestamp: number | bigint): string {
  const ts = typeof timestamp === 'bigint' ? Number(timestamp) : timestamp
  const date = new Date(ts * 1000) // Convert from seconds to milliseconds
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: number | bigint): string {
  const ts = typeof timestamp === 'bigint' ? Number(timestamp) : timestamp
  const date = new Date(ts * 1000)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minutes ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} days ago`
  
  return formatDate(timestamp)
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompactNumber(num: number): string {
  if (num < 1000) return num.toString()
  if (num < 1000000) return `${(num / 1000).toFixed(1)}K`
  if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`
  return `${(num / 1000000000).toFixed(1)}B`
}

/**
 * Parse token amount to bigint
 */
export function parseTokenAmount(
  amount: string,
  decimals: number = 18
): bigint {
  try {
    return parseUnits(amount, decimals)
  } catch {
    return 0n
  }
}

/**
 * Format health ratio with color coding
 */
export function formatHealthRatio(ratio: number): {
  value: string
  status: 'critical' | 'warning' | 'healthy'
  color: string
} {
  const value = `${(ratio * 100).toFixed(0)}%`
  
  if (ratio < 1.2) {
    return { value, status: 'critical', color: 'text-red-600' }
  }
  if (ratio < 1.5) {
    return { value, status: 'warning', color: 'text-yellow-600' }
  }
  return { value, status: 'healthy', color: 'text-green-600' }
}

/**
 * Format expiry status
 */
export function formatExpiryStatus(expiry: bigint): {
  text: string
  isExpired: boolean
  isExpiringSoon: boolean
} {
  const expiryTime = Number(expiry)
  const now = Math.floor(Date.now() / 1000)
  const diff = expiryTime - now
  
  if (diff <= 0) {
    return { text: 'Expired', isExpired: true, isExpiringSoon: false }
  }
  
  const days = Math.floor(diff / 86400)
  const hours = Math.floor((diff % 86400) / 3600)
  
  const isExpiringSoon = days < 7
  
  if (days > 0) {
    return { 
      text: `${days} day${days !== 1 ? 's' : ''} remaining`, 
      isExpired: false, 
      isExpiringSoon 
    }
  }
  
  return { 
    text: `${hours} hour${hours !== 1 ? 's' : ''} remaining`, 
    isExpired: false, 
    isExpiringSoon: true 
  }
}

